import SectionHeading from '@/components/base/SectionHeading';
import ScrollReveal from '@/components/base/ScrollReveal';
import { platformChoices } from '@/mocks/mt4mt5Page';

type PlatformChooserProps = {
  lessons: { mt4: number; mt5: number };
};

export default function PlatformChooser({ lessons }: PlatformChooserProps) {
  return (
    <section id="platforms" className="scroll-mt-24 border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="เลือกแพลตฟอร์ม"
            title="เลือกแพลตฟอร์มที่คุณต้องการเรียนรู้"
            subtitle="ทั้งสองแพลตฟอร์มอยู่ในหน้านี้ทั้งหมด เลือกฝั่งที่ตรงกับโบรกเกอร์ของคุณ แล้วไปยังคู่มือของแพลตฟอร์มนั้นได้ทันที"
          />
          <p className="flex w-fit shrink-0 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2 text-xs font-medium text-foreground-600">
            <i className="ri-information-line text-primary-600" />
            เนื้อหาแยกกัน ไม่ปนกันระหว่างสองแพลตฟอร์ม
          </p>
        </div>

        <div className="mt-9 grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
          {platformChoices.map((platform, index) => {
            const count = index === 0 ? lessons.mt4 : lessons.mt5;
            return (
              <ScrollReveal key={platform.key} delay={index * 90} className="h-full">
                <article className="flex h-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50">
                  <div className="relative h-[210px] w-full overflow-hidden bg-background-100 md:h-[250px]">
                    <img
                      src={platform.image}
                      alt={`${platform.caption} สำหรับการเรียนรู้วิธีใช้ ${platform.name}`}
                      title={`${platform.name} คู่มือภาษาไทย - Forex Academy`}
                      className="h-full w-full object-cover object-top"
                    />
                    <span
                      className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold ${
                        platform.tone === 'primary'
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-accent-500 text-background-50'
                      }`}
                    >
                      {platform.short}
                    </span>
                    <span className="absolute bottom-3 right-4 text-[10px] font-medium text-foreground-600">
                      {platform.caption}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-5 md:p-6">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                        {platform.name}
                      </h3>
                      <span className="flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
                        <i className="ri-file-list-3-line text-sm" />
                        {count} บทความ
                      </span>
                    </div>

                    <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{platform.description}</p>

                    <ul className="mt-4 flex flex-wrap gap-2">
                      {platform.tags.map((tag) => (
                        <li
                          key={tag}
                          className={`rounded-full px-3 py-1 text-xs font-medium ${
                            platform.tone === 'primary'
                              ? 'bg-primary-100 text-primary-800'
                              : 'bg-accent-100 text-accent-800'
                          }`}
                        >
                          {tag}
                        </li>
                      ))}
                    </ul>

                    <a
                      href={`#${platform.target}`}
                      className={`mt-6 inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                        platform.tone === 'primary'
                          ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                          : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                      }`}
                    >
                      {platform.cta}
                      <i className="ri-arrow-down-line text-base" />
                    </a>
                  </div>
                </article>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}